import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Menu, X, GraduationCap, ClipboardList, Gauge, Calculator, Route as RouteIcon,
  Trophy, Gift, Sparkles, Plane, Globe, CheckCircle2, ShieldCheck,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";

const HERO = "https://static.prod-images.emergentagent.com/jobs/933edf10-af87-41b6-8124-d5da806634cd/images/b29474af16e8767abf3fd55ea159051a117ef79244ef542b550c79188cfd0633.jpeg";

const navLinks = [
  { label: "Beranda", href: "#beranda" },
  { label: "Fitur", href: "#fitur" },
  { label: "Beasiswa", href: "#beasiswa" },
  { label: "Test", href: "#fitur" },
  { label: "Kisah Sukses", href: "#kisah" },
  { label: "Harga", href: "#harga" },
];

const features = [
  { icon: GraduationCap, title: "Temukan Beasiswa", desc: "Cari & filter beasiswa yang cocok dengan profilmu.", cta: "Jelajahi" },
  { icon: ClipboardList, title: "Simulasi Test", desc: "Latihan. Diagnosa. Tingkatkan skor bahasa & akademik.", cta: "Mulai Latihan" },
  { icon: Gauge, title: "Cek Readiness", desc: "Ukur kesiapanmu di 5 domain persiapan.", cta: "Cek Sekarang" },
  { icon: Calculator, title: "Kalkulator Biaya", desc: "Hitung estimasi biaya hidup & kuliah otomatis.", cta: "Hitung Biaya" },
  { icon: RouteIcon, title: "Roadmap Persiapan", desc: "Rencana langkah demi langkah menuju kampus impian.", cta: "Buat Roadmap" },
  { icon: Trophy, title: "Kisah Sukses", desc: "Belajar dari perjalanan nyata penerima beasiswa.", cta: "Baca Kisah" },
  { icon: Gift, title: "Benefit Explorer", desc: "Pahami apa saja yang ditanggung tiap beasiswa.", cta: "Lihat Benefit" },
];

const stats = [
  { value: "10.000+", label: "Mahasiswa Terbantu" },
  { value: "500+", label: "Beasiswa Terkurasi" },
  { value: "50+", label: "Negara Tujuan" },
  { value: "95%", label: "Kepuasan Pengguna" },
];

function FloatCard({ className, children, delay = 0 }) {
  return (
    <div
      className={`animate-float-slow rounded-2xl border border-purple-100 bg-white/90 p-3 shadow-lg shadow-purple-900/5 backdrop-blur ${className}`}
      style={{ animationDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

export default function Landing() {
  const [open, setOpen] = useState(false);
  const { user } = useApp();
  const navigate = useNavigate();
  const CHECKOUT = "https://lynk.id/scholavia/ne6kpwmkr1km";
  const goStart = () => { if (user) navigate("/app"); else window.location.assign(CHECKOUT); };

  return (
    <div className="min-h-screen bg-white" id="beranda">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-purple-100 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <nav className="hidden items-center gap-7 lg:flex">
            {navLinks.map((l) => (
              <a key={l.label} href={l.href} className="text-sm font-medium text-slate-600 transition-colors hover:text-[#5B21B6]">{l.label}</a>
            ))}
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="header-login" className="text-slate-700 hover:text-[#5B21B6]">Masuk</Button>
            <Button onClick={goStart} data-testid="header-register" className="rounded-full bg-[#7C3AED] px-5 shadow-md shadow-purple-600/25 hover:bg-[#6D28D9]">Beli Sekarang</Button>
          </div>
          <button className="rounded-lg p-2 text-slate-600 lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {open && (
          <div className="border-t border-purple-100 bg-white px-4 py-4 lg:hidden">
            <div className="flex flex-col gap-1">
              {navLinks.map((l) => (
                <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-purple-50">{l.label}</a>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => navigate("/login")}>Masuk</Button>
              <Button className="flex-1 rounded-full bg-[#7C3AED]" onClick={goStart}>Beli Sekarang</Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-0 h-96 w-96 rounded-full bg-purple-200/40 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 h-80 w-80 rounded-full bg-fuchsia-100/50 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-2 lg:gap-6 lg:px-8 lg:py-20">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-xs font-semibold text-[#6D28D9]">
              <Sparkles className="h-3.5 w-3.5" /> Your Study Abroad Journey Starts Here
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              From Dream to <span className="bg-gradient-to-r from-[#5B21B6] to-[#8B5CF6] bg-clip-text text-transparent">Destination.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-600 sm:text-lg">
              Temukan beasiswa, ukur kesiapanmu, hitung biaya, dan susun langkah menuju kampus impianmu, semuanya dalam satu platform.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Button size="lg" onClick={goStart} data-testid="hero-cta-primary" className="rounded-full bg-[#7C3AED] px-7 text-base shadow-lg shadow-purple-600/25 hover:bg-[#6D28D9]">
                Mulai Sekarang <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/login")} data-testid="hero-cta-secondary" className="rounded-full border-purple-200 px-7 text-base text-[#5B21B6] hover:bg-purple-50">
                Masuk
              </Button>
            </div>
            <div className="mt-9 grid max-w-lg grid-cols-2 gap-4 sm:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl font-extrabold text-[#5B21B6]">{s.value}</div>
                  <div className="text-xs text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative mx-auto max-w-md">
              <div className="absolute inset-0 -z-10 rounded-[2.5rem] bg-gradient-to-br from-purple-100 via-fuchsia-50 to-transparent blur-2xl" />
              <img src={HERO} alt="Mahasiswa Indonesia siap studi ke luar negeri" className="w-full rounded-[2rem] object-contain" loading="eager" />
              <FloatCard className="absolute -left-2 top-10 sm:-left-6" delay={0.2}>
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600"><CheckCircle2 className="h-4 w-4" /></span>
                  <div><p className="text-[11px] text-slate-400">Scholarship Match</p><p className="text-xs font-bold text-slate-800">78% Chevening</p></div>
                </div>
              </FloatCard>
              <FloatCard className="absolute -right-2 top-24 sm:-right-6" delay={0.6}>
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 text-[#7C3AED]"><Gauge className="h-4 w-4" /></span>
                  <div><p className="text-[11px] text-slate-400">Readiness</p><p className="text-xs font-bold text-slate-800">72%</p></div>
                </div>
              </FloatCard>
              <FloatCard className="absolute -left-2 bottom-16 sm:-left-8" delay={1}>
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-fuchsia-100 text-fuchsia-600"><Plane className="h-4 w-4" /></span>
                  <div><p className="text-[11px] text-slate-400">Next Step</p><p className="text-xs font-bold text-slate-800">IELTS Prep</p></div>
                </div>
              </FloatCard>
              <FloatCard className="absolute -right-2 bottom-6 sm:-right-6" delay={1.4}>
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600"><Globe className="h-4 w-4" /></span>
                  <div><p className="text-[11px] text-slate-400">Estimated Cost</p><p className="text-xs font-bold text-slate-800">£1.250/bln</p></div>
                </div>
              </FloatCard>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="fitur" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Semua yang kamu butuhkan, satu platform</h2>
          <p className="mt-3 text-slate-600">Dari menemukan peluang hingga melacak persiapan, Scholavia menuntun setiap langkahmu.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => (
            <div key={f.title} className="group rounded-2xl border border-purple-100 bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md hover:shadow-purple-900/5" data-testid={`feature-${f.title}`}>
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#F3E8FF] to-[#EDE9FE] text-[#7C3AED]">
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-base font-bold text-slate-900">{f.title}</h3>
              <p className="mt-1 text-sm text-slate-500">{f.desc}</p>
              <button onClick={goStart} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#7C3AED] hover:gap-2 transition-all">
                {f.cta} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Trust / beasiswa strip */}
      <section id="beasiswa" className="bg-gradient-to-br from-[#FBFAFF] to-[#F5F3FF] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-purple-100 bg-white p-8 text-center shadow-sm sm:p-12">
            <ShieldCheck className="mx-auto h-10 w-10 text-[#7C3AED]" />
            <h2 className="mt-4 font-display text-2xl font-bold text-slate-900 sm:text-3xl">Data beasiswa dengan sumber & verifikasi</h2>
            <p className="mx-auto mt-3 max-w-2xl text-slate-600">
              Setiap beasiswa dilengkapi status verifikasi, sumber resmi, dan catatan. Skor match & readiness adalah <b>diagnostik persiapan</b>, bukan probabilitas penerimaan.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2 text-sm">
              {["Chevening", "LPDP", "Erasmus Mundus", "Australia Awards", "MEXT"].map((n) => (
                <span key={n} className="rounded-full border border-purple-200 bg-purple-50 px-3 py-1 font-medium text-[#6D28D9]">{n}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Kisah / Harga CTA */}
      <section id="harga" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div id="kisah" className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#5B21B6] via-[#7C3AED] to-[#8B5CF6] p-10 text-center sm:p-16">
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
          <h2 className="font-display text-3xl font-extrabold text-white sm:text-4xl">Mulai perjalananmu hari ini</h2>
          <p className="mx-auto mt-3 max-w-xl text-purple-100">Semua fitur inti dalam satu platform untuk menemani persiapan studimu.</p>
          <Button size="lg" onClick={goStart} data-testid="cta-bottom" className="mt-7 rounded-full bg-white px-8 text-base font-bold text-[#5B21B6] hover:bg-purple-50">
            Mulai Sekarang <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-purple-100 bg-white py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:flex-row sm:px-6 lg:px-8">
          <Logo />
          <p className="text-sm text-slate-400">© {new Date().getFullYear()} Scholavia. From Dream to Destination.</p>
        </div>
      </footer>
    </div>
  );
}
